<script setup lang="ts">
import { computed, ref } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";

import { getMe, listObservations, raiseObservation } from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import { useProjectStore } from "@/stores/project";

const router = useRouter();
const queryClient = useQueryClient();
const { selectedProjectId } = storeToRefs(useProjectStore());

// Clients get a read-only register (no raising observations).
const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
const isClient = computed(() => (me.value?.roles ?? []).includes("client"));

const kindFilters = [
  { value: "", label: "All" },
  { value: "close_call", label: "Close calls" },
  { value: "good_practice", label: "Good practice" },
];
const kindFilter = ref<string>("");

const { data, isPending, isError, error } = useQuery({
  queryKey: ["observations", selectedProjectId, kindFilter],
  queryFn: () =>
    listObservations(selectedProjectId.value, kindFilter.value || undefined),
});

const kindLabel = (kind: string): string =>
  kind === "close_call" ? "Close call" : "Good practice";
const statusLabel = (s: string): string => s.replace("_", " ");

const open = (id: string): void => {
  void router.push({
    name: "observation-detail",
    params: { projectId: selectedProjectId.value, observationId: id },
  });
};

// ---- Office fallback: raise from the web ----
const showRaise = ref(false);
const form = ref({ kind: "close_call", description: "", type: "", location: "" });
const raiseBusy = ref(false);
const raiseError = ref<string | null>(null);
const canRaise = computed(() => form.value.description.trim().length > 0);

async function raise(): Promise<void> {
  if (!canRaise.value) return;
  raiseBusy.value = true;
  raiseError.value = null;
  try {
    const id = await raiseObservation({
      projectId: selectedProjectId.value as string,
      kind: form.value.kind,
      description: form.value.description.trim(),
      observationType: form.value.type.trim() || undefined,
      locationRef: form.value.location.trim() || undefined,
    });
    await queryClient.invalidateQueries({ queryKey: ["observations"] });
    open(id);
  } catch (e) {
    raiseError.value = e instanceof Error ? e.message : String(e);
  } finally {
    raiseBusy.value = false;
  }
}
</script>

<template>
  <PageLayout>
    <div class="mb-4 flex items-center justify-between gap-4">
      <h1 class="text-xl font-semibold">
        Close calls &amp; good practice
      </h1>
      <div class="flex items-center gap-3">
        <div class="flex gap-1">
          <button
            v-for="f in kindFilters"
            :key="f.value"
            type="button"
            class="text-sm rounded-md px-3 py-1"
            :class="kindFilter === f.value
              ? 'bg-brand-primary text-brand-primary-on'
              : 'bg-neutral-100 text-neutral-500 hover:text-neutral-900'"
            @click="kindFilter = f.value"
          >
            {{ f.label }}
          </button>
        </div>
        <button
          v-if="!isClient"
          type="button"
          class="shrink-0 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
          @click="showRaise = !showRaise"
        >
          {{ showRaise ? "Cancel" : "+ Raise" }}
        </button>
      </div>
    </div>

    <div
      v-if="showRaise && !isClient"
      class="mb-4 rounded-xl border border-neutral-200 bg-neutral-0 p-4"
    >
      <p class="mb-3 text-sm text-neutral-500">
        Report a close call (near-miss) or good practice — for learning, no blame.
        Set owner and actions after creating, on its page.
      </p>
      <div class="grid gap-3">
        <div class="flex gap-2">
          <button
            v-for="k in [{ v: 'close_call', l: 'Close call' }, { v: 'good_practice', l: 'Good practice' }]"
            :key="k.v"
            type="button"
            class="rounded-md px-3 py-1.5 text-sm font-medium"
            :class="form.kind === k.v
              ? 'bg-brand-primary text-brand-primary-on'
              : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'"
            @click="form.kind = k.v"
          >
            {{ k.l }}
          </button>
        </div>
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">What happened?</span>
          <textarea
            v-model="form.description"
            rows="2"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          />
        </label>
        <div class="grid gap-3 sm:grid-cols-2">
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Type (optional)</span>
            <input
              v-model="form.type"
              type="text"
              placeholder="e.g. electrical, access, housekeeping"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            >
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Location</span>
            <input
              v-model="form.location"
              type="text"
              placeholder="e.g. B2585"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            >
          </label>
        </div>
        <div class="flex items-center gap-3">
          <button
            type="button"
            class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
            :disabled="!canRaise || raiseBusy"
            @click="raise"
          >
            {{ raiseBusy ? "Raising…" : "Raise" }}
          </button>
          <span
            v-if="raiseError"
            class="text-sm text-semantic-danger"
          >{{ raiseError }}</span>
        </div>
      </div>
    </div>

    <div
      v-if="isPending"
      class="text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="bg-semantic-danger/10 text-semantic-danger rounded-md p-4"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data && data.length === 0"
      class="text-neutral-500"
    >
      No records in this view.
    </div>
    <div
      v-else-if="data"
      class="rounded-xl bg-neutral-0 shadow overflow-hidden"
    >
      <table class="w-full text-sm">
        <thead class="bg-neutral-50 text-neutral-500 text-left">
          <tr>
            <th class="px-4 py-2 font-medium">
              Raised
            </th>
            <th class="px-4 py-2 font-medium">
              Kind
            </th>
            <th class="px-4 py-2 font-medium">
              Description
            </th>
            <th class="px-4 py-2 font-medium">
              Location
            </th>
            <th class="px-4 py-2 font-medium">
              Assigned
            </th>
            <th class="px-4 py-2 font-medium">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="o in data"
            :key="o.id"
            class="border-t border-neutral-100 align-top cursor-pointer hover:bg-neutral-50"
            @click="open(o.id)"
          >
            <td class="px-4 py-2 whitespace-nowrap">
              {{ o.raised_at.slice(0, 10) }}
            </td>
            <td class="px-4 py-2">
              <span
                class="rounded-full px-2 py-0.5 text-xs"
                :class="o.kind === 'close_call'
                  ? 'bg-semantic-warning/10 text-semantic-warning'
                  : 'bg-semantic-success/10 text-semantic-success'"
              >{{ kindLabel(o.kind) }}</span>
            </td>
            <td class="px-4 py-2">
              {{ o.description }}
            </td>
            <td class="px-4 py-2">
              {{ o.location_ref ?? "—" }}
            </td>
            <td class="px-4 py-2 whitespace-nowrap">
              {{ o.assigned_to_name ?? "—" }}
            </td>
            <td class="px-4 py-2 capitalize">
              {{ statusLabel(o.status) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </PageLayout>
</template>
